import React from "react";
import numberToWord from "../../components/common/utlity/invoiceword";
import {
  SaleformatDateByBusinessPreference,
  truncateCommaSeparatedString,
} from "../../components/common/utlity/utility";
import { useTranslation } from "react-i18next";
import { AWS_URL } from "../../services/http.service";
import { Country, State } from "country-state-city";

const GstTheme1 = ({
  containerRef,
  businessName,
  phone,
  item,
  selectedColor,
  bussiness,
  darkMode = false,
}) => {
  const amountInwords = numberToWord(item?.transactionAmount || 0);
  const { t } = useTranslation();

  // Country & State mapping
  const businessCountry = bussiness?.country
    ? Country.getCountryByCode(bussiness.country)
    : null;
  const businessState = bussiness?.state
    ? State.getStateByCodeAndCountry(bussiness.state, bussiness.country)
    : null;

  const customerCountry = item?.country
    ? Country.getCountryByCode(item.country)
    : null;
  const customerState = item?.state
    ? State.getStateByCodeAndCountry(item.state, item.country)
    : null;

  // Color Map
  const colorHexMap = {
    "bg-green-400": "#68D391",
    "bg-pink-400": "#F687B3",
    "bg-blue-400": "#63B3ED",
    "bg-yellow-400": "#F6E05E",
    "bg-red-400": "#FC8181",
    "bg-cyan-600": "#0891b2",
    "bg-sky-800": "#075985",
    "bg-slate-600": "#475569",
    "bg-gray-800": "#1f2937",
    "bg-lime-300": "#bef264",
    "bg-blue-800": "#1e40af",
    "bg-green-700": "#15803d",
    "bg-lime-800": "#3f6212",
    "bg-red-950": "#450a0a",
    "bg-purple-800": "#6b21a8",
    "bg-pink-500": "#ec4899",
    "bg-amber-800": "#92400e",
    "bg-yellow-700": "#a16207",
    "bg-fuchsia-800": "#86198f",
    "bg-pink-700": "#be185d",
    "bg-yellow-200": "#fef08a",
    "bg-yellow-950": "#422006",
    "bg-fuchsia-500": "#d946ef",
    "bg-rose-900": "#881337",
    "bg-teal-800": "#115e59",
    "bg-emerald-900": "#064e3b",
    "bg-cyan-900": "#164e63",
    "bg-white": "#ffffff",
  };

  const themeStyles = {
  background: "#eff6ff",
  textPrimary: "#000000",
  border: "#000000",
  headerBg: colorHexMap?.[selectedColor] ?? "#cb39db",
  rowBg: "#ffffff",
};

  // Total discount calculation
  const totalDiscount = (item?.items || item?.returnitems || []).reduce(
    (sum, itm) => sum + (itm.discount || 0),
    0
  );

  return (
    <div
      className="p-4"
      ref={containerRef}
      style={{
        backgroundColor: themeStyles.background,
        color: themeStyles.textPrimary,
      }}
    >
      {/* Business Header */}
      <div className="flex items-center gap-2">
        <span className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100 shadow">
          <img
            src={
              bussiness?.businesslogo
                ? AWS_URL + bussiness.businesslogo
                : "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT3dSEtDvpd9KAn4SdFJVoL0K1lj4Xfc-wVWQ&s"
            }
            alt="Logo"
            className="w-full h-full object-contain"
            //crossOrigin="anonymous"
          />
        </span>
        <span className="font-calibri text-2xl font-bold">{businessName}</span>
      </div>

      <p className="mt-2 text-sm">
        {t("NEWINVOICE.PHONE")}: {phone || "0000000000"}
      </p>
      <p className="text-sm">
        {bussiness?.firstLine
          ? `${bussiness.firstLine}, ${businessState?.name || ""}, ${
              businessCountry?.name || ""
            }`
          : "N/A"}
      </p>
       <p>Email:{bussiness?.email || "No Email"}</p>
        <p>GST: {bussiness?.gstNumber || "N/A"}</p>

      <hr
        className="my-3"
        style={{ borderColor: themeStyles.headerBg, borderWidth: "2px" }}
      />

      {/* Invoice Title */}
      <div className="text-center text-xl font-bold">
        {t("NEWINVOICE.TAX_INVOICE")}
      </div>

      {/* Customer + Invoice Info */}
      <div className="flex justify-between mt-4 leading-6">
        <div>
          <span className="font-bold">{t("NEWINVOICE.BILL_TO")}</span>
          <div>{item.customerName}</div>
          <div>{item.mobile}</div>
          <div>
            {item?.address
              ? `${item.address}, ${customerState?.name || ""}, ${
                  customerCountry?.name || ""
                }`
              : "N/A"}
          </div>
        </div>
        <div className="text-right">
          <div>
            {t("NEWINVOICE.NO")}: {item.invoiceId}
          </div>
          <div>
            {t("NEWINVOICE.DATE")}:{" "}
            {SaleformatDateByBusinessPreference(
              item?.saleDate,
              bussiness?.dateFormat
            )}
          </div>
        </div>
      </div>

      {/* Items Table */}
      <table className="border border-black border-collapse w-full mt-4">
        <thead>
          <tr style={{ backgroundColor: themeStyles.headerBg }}>
            <th className="border border-black p-2">#</th>
            <th className="border border-black p-2">
              {t("NEWINVOICE.ITEM_NAME")}
            </th>
            <th className="border border-black p-2">
              {t("NEWINVOICE.HSN_SAC")}
            </th>
            <th className="border border-black p-2">{t("NEWINVOICE.QTY")}</th>
            <th className="border border-black p-2">
              {t("NEWINVOICE.PRICE_PER_UNIT")}
            </th>
            <th className="border border-black p-2">
              {t("NEWINVOICE.DISCOUNT")}
            </th>
            <th className="border border-black p-2">{t("NEWINVOICE.GST")}</th>
            <th className="border border-black p-2">
              {t("NEWINVOICE.AMOUNT")}
            </th>
          </tr>
        </thead>
        <tbody>
          {(item?.invoiceId?.startsWith("RT")
            ? item?.returnitems
            : item?.items
          )?.map((itm, index) => (
            <tr key={index} style={{ backgroundColor: themeStyles.rowBg }}>
              <td className="border border-black p-2">{index + 1}</td>
              <td className="border border-black p-2 font-bold">
                {itm.productName}
              </td>
              <td className="border border-black p-2">
                {truncateCommaSeparatedString(itm.hsnNumber) || "N/A"}
              </td>
              <td className="border border-black p-2">{itm.quantity}</td>
              <td className="border border-black p-2">{itm.salePrice}</td>
              <td className="border border-black p-2">
                {itm.discount || 0} {bussiness.currency}
              </td>
              <td className="border border-black p-2">{itm.igst}%</td>
              <td className="border border-black p-2">{itm.finalPrice}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals Section */}
      <div className="grid grid-cols-2 gap-4 mt-4">
        <div className="col-span-1 p-4">
          <div className="font-bold">{t("NEWINVOICE.AMOUNT_IN_WORDS")}</div>
          <div className="text-sm">{amountInwords}</div>
          <div className="font-bold mt-2">
            {t("NEWINVOICE.TERMS_CONDITIONS")}
          </div>
          <div className="text-sm">{t("NEWINVOICE.THANKS_MESSAGE")}</div>
        </div>
        <div className="col-span-1 p-4">
          <div className="flex justify-between mb-2">
            <span>{t("NEWINVOICE.SUB_TOTAL")}:</span>
            <span>₹ {item.transactionAmount}</span>
          </div>

          {item.cgst > 0 && (
            <div className="flex justify-between mb-2">
              <span>{t("TAX_WITHOUT_PERC.CGST")}:</span>
              <span>₹ {parseFloat(item.cgst).toFixed(2)}</span>
            </div>
          )}

          {item.sgst > 0 && (
            <div className="flex justify-between mb-2">
              <span>{t("TAX_WITHOUT_PERC.SGST")}:</span>
              <span>₹ {parseFloat(item.sgst).toFixed(2)}</span>
            </div>
          )}

          {item.igst > 0 && (
            <div className="flex justify-between mb-2">
              <span>{t("TAX_WITHOUT_PERC.IGST")}:</span>
              <span>₹ {parseFloat(item.igst).toFixed(2)}</span>
            </div>
          )}

          {item.cess > 0 && (
            <div className="flex justify-between mb-2">
              <span>{t("TAX_WITHOUT_PERC.CESS")}:</span>
              <span>₹ {parseFloat(item.cess).toFixed(2)}</span>
            </div>
          )}
          {/* <div className="flex justify-between mb-2">
            <span>{t("NEWINVOICE.TOTAL_DISCOUNT")}:</span>
            <span>₹ {totalDiscount}</span>
          </div> */}
          <hr className="border-gray-400" />
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-between items-start mt-5">
        {bussiness?.aadharPhoto && (
          <div className="flex w-1/2 p-2 bg-gray-100 rounded-lg">
            <img
              src={AWS_URL + bussiness?.aadharPhoto}
              alt="Aadhar"
              className="w-28 object-contain"
              //crossOrigin="anonymous"
            />
          </div>
        )}
        <div className="text-right">
          <div>{t("NEWINVOICE.FOR_COMPANY", { company: businessName })}</div>
          <div className="font-bold">
            {t("NEWINVOICE.AUTHORIZED_SIGNATORY")}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GstTheme1;
